/**
 * Delivery Split Generator Component
 * Version: 1.0.0
 * Last Updated: 2025-12-24
 *
 * Description:
 *   Generate printable PDF delivery run sheets from Excel/CSV files
 *   - File upload with preview
 *   - Tabular preview of delivery details
 *   - One-click PDF generation
 */

import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Alert,
    CircularProgress,
    Chip,
} from '@mui/material';
import {
    Upload as UploadIcon,
    Download as DownloadIcon,
    CheckCircle as CheckCircleIcon,
    Fullscreen as FullscreenIcon,
    FullscreenExit as FullscreenExitIcon,
    Description as DescriptionIcon
} from '@mui/icons-material';
import { DataGrid, GridColDef, useGridApiRef } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import { b2cOpsAPI } from '../../api';

interface PreviewRow {
    id: number;
    order_no?: string;
    name?: string;
    mobile?: string;
    address?: string;
    delivery_date?: string;
    slot?: string;
    [key: string]: any;
}

interface PreviewData {
    total_entries: number;
    preview_data: PreviewRow[];
    all_data: any[];
    columns_found: string[];
}

export default function DeliverySplitGenerator() {
    const { enqueueSnackbar } = useSnackbar();
    const gridRef = useGridApiRef();

    // State
    const [file, setFile] = useState<File | null>(null);
    const [previewData, setPreviewData] = useState<PreviewData | null>(null);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);

    // Drag and drop handlers
    const [isDragging, setIsDragging] = useState(false);

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);

        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            validateAndLoadFile(droppedFile);
        }
    };

    const validateAndLoadFile = async (uploadedFile: File) => {
        // Validate file type
        const validTypes = ['.xlsx', '.xls', '.csv'];
        const fileExt = uploadedFile.name.substring(uploadedFile.name.lastIndexOf('.')).toLowerCase();

        if (!validTypes.includes(fileExt)) {
            enqueueSnackbar('Invalid file type. Please upload Excel or CSV file.', { variant: 'error' });
            return;
        }

        // Validate file size (20MB)
        if (uploadedFile.size > 20 * 1024 * 1024) {
            enqueueSnackbar('File too large. Maximum size is 20 MB.', { variant: 'error' });
            return;
        }

        setFile(uploadedFile);
        setLoading(true);

        try {
            const preview = await b2cOpsAPI.previewDeliverySplit(uploadedFile);
            setPreviewData(preview);
            enqueueSnackbar('File loaded successfully!', { variant: 'success' });
        } catch (error: any) {
            enqueueSnackbar(error.response?.data?.detail || 'Failed to load file', { variant: 'error' });
            setFile(null);
            setPreviewData(null);
        } finally {
            setLoading(false);
        }
    };

    // Handle file upload (click)
    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const uploadedFile = event.target.files?.[0];
        if (uploadedFile) {
            validateAndLoadFile(uploadedFile);
        }
    };

    // Handle generation
    const handleGenerate = async () => {
        if (!previewData) return;

        setGenerating(true);
        try {
            const response = await b2cOpsAPI.generateDeliverySplit(previewData.all_data);
            const blob = response.data;

            // Download the file
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            const contentDisposition = response.headers['content-disposition'];
            let filename = 'delivery_sheet.pdf';

            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }

            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            enqueueSnackbar('Delivery sheet generated successfully!', { variant: 'success' });
        } catch (error: any) {
            enqueueSnackbar(error.response?.data?.detail || 'Failed to generate PDF', { variant: 'error' });
        } finally {
            setGenerating(false);
        }
    };

    // DataGrid columns
    const columns: GridColDef[] = [
        { field: 'id', headerName: '#', width: 60 },
        { field: 'order_no', headerName: 'Order #', width: 100 },
        { field: 'name', headerName: 'Customer Name', width: 220 },
        { field: 'mobile', headerName: 'Mobile', width: 130 },
        { field: 'address', headerName: 'Address', width: 300 },
        { field: 'delivery_date', headerName: 'Date', width: 110 },
        { field: 'slot', headerName: 'Slot', width: 80 },
    ];

    // Prepare rows
    const rows = previewData?.preview_data?.map((item, idx) => ({
        id: idx + 1,
        ...item
    })) || [];

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DescriptionIcon fontSize="large" color="primary" /> Delivery Split Generator
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Generate printable delivery run sheets from Excel/CSV files
                </Typography>
            </Box>

            {/* File Upload */}
            <Paper
                sx={{
                    p: 4,
                    mb: 4,
                    border: isDragging ? '2px dashed #1976d2' : '2px dashed #e0e0e0',
                    bgcolor: isDragging ? 'action.hover' : 'background.paper',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        borderColor: '#1976d2',
                        bgcolor: '#f5f9ff'
                    }
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <input
                    accept=".xlsx,.xls,.csv"
                    style={{ display: 'none' }}
                    id="file-upload"
                    type="file"
                    onChange={handleFileUpload}
                />
                <label htmlFor="file-upload">
                    <Box component="span" sx={{ display: 'block', pointerEvents: 'none' }}>
                        {loading ? (
                            <CircularProgress />
                        ) : (
                            <>
                                <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                                <Typography variant="h6" gutterBottom>
                                    Click or Drag file to this area to upload
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Support for single or bulk upload. Strictly Excel or CSV files.
                                </Typography>
                            </>
                        )}
                    </Box>
                </label>
                {file && !loading && (
                    <Chip
                        icon={<CheckCircleIcon />}
                        label={file.name}
                        color="success"
                        variant="outlined"
                        sx={{ mt: 2 }}
                    />
                )}
            </Paper>

            {/* Preview Section */}
            {previewData && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        📊 Summary & Preview
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 4, mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Box>
                            <Typography variant="body2" color="text.secondary">Total Entries</Typography>
                            <Typography variant="h5" fontWeight="bold">{previewData.total_entries}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" color="text.secondary">Columns Found</Typography>
                            <Typography variant="body1">
                                {previewData.columns_found.join(', ')}
                            </Typography>
                        </Box>
                    </Box>


                    <Box
                        className={isFullscreen ? 'datagrid-fullscreen' : ''}
                        sx={{
                            height: isFullscreen ? '100vh' : 500,
                            width: '100%',
                            transition: 'height 0.3s ease',
                        }}
                    >
                        <DataGrid
                            apiRef={gridRef}
                            rows={rows}
                            columns={columns}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 20 } },
                            }}
                            pageSizeOptions={[20, 50, 100]}
                            disableRowSelectionOnClick
                            slots={{
                                toolbar: () => (
                                    <Box sx={{ p: 1, display: 'flex', gap: 1, alignItems: 'center', borderBottom: '1px solid #e0e0e0' }}>
                                        <Box sx={{ flexGrow: 1 }} />
                                        <Button
                                            startIcon={isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                                            onClick={() => setIsFullscreen(!isFullscreen)}
                                            size="small"
                                        >
                                            {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                                        </Button>
                                    </Box>
                                ),
                            }}
                            sx={{
                                border: '1px solid #e0e0e0',
                                '& .MuiDataGrid-columnHeaders': {
                                    bgcolor: '#f5f5f5',
                                },
                            }}
                        />
                    </Box>

                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                        onClick={handleGenerate}
                        disabled={generating}
                        fullWidth
                        sx={{ mt: 3, py: 1.5, fontSize: '1.1rem' }}
                    >
                        {generating ? 'Generating PDF...' : `Generate Delivery Sheet (${previewData.total_entries} orders)`}
                    </Button>
                </Paper>
            )}
        </Box>
    );
}
